import type { ReactNode } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { isUtcCalendarDate, type ProductProcessingUsage as Usage } from "@/lib/product-processing-usage";

interface ProductProcessingUsageProps {
  /** Already validated and normalized by getMonthlyPlanUsage. */
  value?: Usage;
  loading?: boolean;
  unavailable?: boolean;
  /** Inclusive period end as a validated UTC calendar date (YYYY-MM-DD). */
  periodEnd?: string;
}

export function ProductProcessingUsage({ value, loading = false, unavailable = false, periodEnd }: ProductProcessingUsageProps) {
  const usage = unavailable ? undefined : value;
  // The comparison is only meaningful for a real UTC calendar date; treat anything else as an unknown period.
  const beforeTracking = Boolean(usage && isUtcCalendarDate(periodEnd)
    && periodEnd < usage.trackingStartedAt.slice(0, 10));
  let content: ReactNode;
  if (loading) {
    content = <Skeleton className="h-6 w-24" />;
  } else if (!usage) {
    content = (
      <p role={unavailable ? undefined : "status"}
        className={`text-sm ${unavailable ? "text-red-500" : "text-muted-foreground"}`}>
        Product processing usage is unavailable.
      </p>
    );
  } else if (beforeTracking) {
    content = <p className="text-sm text-muted-foreground">Tracking had not started for this period.</p>;
  } else {
    content = <p className="text-2xl font-semibold tabular-nums">{usage.completed.toLocaleString()}</p>;
  }

  return (
    <section aria-label="Processed products" className="space-y-2 rounded-lg border p-4">
      <h3 className="text-sm font-medium">Processed products</h3>
      {content}
      {!loading && usage && (
        <p className="text-xs text-muted-foreground">
          Tracked from <time dateTime={usage.trackingStartedAt}>{new Date(usage.trackingStartedAt).toLocaleDateString(undefined, {
            timeZone: "UTC", year: "numeric", month: "short", day: "numeric",
          })}</time> (UTC).{!beforeTracking && " Counts successful processing across all catalogues. A product processed again counts again."}
        </p>
      )}
    </section>
  );
}
